import type { AssetsLibraryRef } from "@v2/features/assets/components/AssetsLibrary";
import type { Asset } from "schemas/generated/asset";
import type { ModifiableElementType } from "schemas/generated/fileModifiers";

import React, { useState } from "react";

import {
  PopoverContent,
  PopoverRoot,
  PopoverTrigger,
} from "@replo/design-system/components/popover/Popover";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import AssetsLibrary from "@v2/features/assets/components/AssetsLibrary";
import { useChatFileDropZone } from "@v2/features/chat/hooks/useChatFileDropZone";
import { ChevronUp, Plus } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";

import { useIsSelectedElementDynamic } from "../hooks/useIsSelectedElementDynamic";
import { useDefaultSelectedElementDesignation } from "../store/ModifiersStoreContext";

interface AssetModifierProps {
  onAssetChange: (assetSource: string | null) => void;
}

export const AssetModifier: React.FC<AssetModifierProps> = ({
  onAssetChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const assetsLibraryRef = React.useRef<AssetsLibraryRef>(null);

  const defaultDesignation = useDefaultSelectedElementDesignation();
  const { isDynamic, isDynamicProduct } = useIsSelectedElementDynamic();

  const [selectedAssetUrl, setSelectedAssetUrl] = useOverridableState<
    string | null
  >(defaultDesignation?.element.props?.src ?? null, onAssetChange);

  const assetUrl = selectedAssetUrl;

  const allowedAssetTypes: ("image" | "video")[] = (() => {
    if (defaultDesignation?.element.type === "image") {
      return ["image"];
    }
    if (defaultDesignation?.element.type === "video") {
      return ["video"];
    }
    return ["image", "video"];
  })();

  // NOTE (Kevin, 2025-12-15): Register as an interceptor for file drops when popover is open
  useChatFileDropZone({
    isActive: isOpen,
    acceptedTypes: allowedAssetTypes,
    onFileDrop: (files) => {
      assetsLibraryRef.current?.uploadFiles(files);
    },
  });

  const handleAssetSelect = (asset: Asset) => {
    setSelectedAssetUrl(asset.url);
    setIsOpen(false);
  };

  const popoverContent = (
    <div
      className="flex w-full flex-col min-h-0 bg-white rounded-lg shadow-lg"
      style={{
        width: "var(--anchor-width)",
        height: "400px",
      }}
    >
      <AssetsLibrary
        ref={assetsLibraryRef}
        assetTypes={allowedAssetTypes}
        onAssetSelected={handleAssetSelect}
        shouldAllowActions={false}
        isDropDisabled
      />
    </div>
  );

  const assetSelector = (
    <div className="w-full">
      <div
        className={twMerge(
          "flex flex-row items-center justify-between p-2 gap-2 w-full h-12 bg-surface border-[0.5px] border-border rounded-lg cursor-pointer hover:bg-background/50 transition-colors",
          (isDynamic || isDynamicProduct) &&
            "opacity-50 cursor-not-allowed pointer-events-none",
        )}
        onClick={() => !isDynamic && !isDynamicProduct && setIsOpen(true)}
      >
        <div className="flex flex-row items-center gap-2">
          <div className="box-content flex flex-row justify-center items-center w-8 h-8 bg-white border-[0.5px] border-dashed rounded-md overflow-hidden">
            {assetUrl ? (
              <AssetPreview
                assetUrl={assetUrl}
                elementType={defaultDesignation?.element.type}
              />
            ) : (
              <Plus className="w-4 h-4 text-muted" />
            )}
          </div>
          <span className="text-xs font-medium text-muted">
            {assetUrl ? "Change Media" : "Add Media"}
          </span>
        </div>
        <ChevronUp
          className={twMerge(
            "text-muted transition-transform",
            isOpen && "rotate-180",
          )}
          size={12}
        />
      </div>
    </div>
  );

  if (isDynamic || isDynamicProduct) {
    return (
      <DynamicElementTooltip
        isDynamic={isDynamic}
        isDynamicProduct={isDynamicProduct}
      >
        {assetSelector}
      </DynamicElementTooltip>
    );
  }

  return (
    <PopoverRoot open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger render={<div className="w-full" />}>
        {assetSelector}
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        showCloseButton={false}
        UNSAFE_className="bg-transparent border-0 p-0 shadow-none w-auto rounded-none gap-0"
      >
        {popoverContent}
      </PopoverContent>
    </PopoverRoot>
  );
};

const AssetPreview: React.FC<{
  assetUrl: string;
  elementType?: ModifiableElementType;
}> = ({ assetUrl, elementType }) => {
  if (elementType === "video") {
    return (
      <video
        src={assetUrl}
        className="object-cover rounded-sm w-full h-full"
        preload="metadata"
        muted
      />
    );
  }
  return (
    <img
      src={assetUrl}
      alt="Selected media"
      className="object-cover rounded-sm w-full h-full"
    />
  );
};

/**
 * Wrapper component that displays a tooltip for disabled dynamic elements.
 * Use this to wrap any modifier control that should be disabled when the element is dynamic.
 * Shows different messages based on whether the element is product-specific dynamic, general dynamic,
 * or has nested children with data-rid attributes.
 */
export const DynamicElementTooltip: React.FC<
  React.PropsWithChildren<{
    isDynamic: boolean;
    isDynamicProduct?: boolean;
    hasDataRidChildren?: boolean;
  }>
> = ({
  children,
  isDynamic,
  isDynamicProduct = false,
  hasDataRidChildren = false,
}) => {
  const shouldShowTooltip = isDynamic || isDynamicProduct || hasDataRidChildren;

  if (!shouldShowTooltip) {
    return <>{children}</>;
  }

  const getTooltipMessage = () => {
    if (hasDataRidChildren) {
      return "Edit text on page";
    }
    if (isDynamicProduct) {
      return "Edit in Products tab";
    }
    return "Request edits through chat";
  };
  const tooltipMessage = getTooltipMessage();

  return (
    <Tooltip content={tooltipMessage} triggerAsChild>
      <div className="cursor-not-allowed" tabIndex={0}>
        {children}
      </div>
    </Tooltip>
  );
};
